import { List, ListItem, Typography, Divider, Box } from '@mui/material';
import React from 'react';
import { Currencies, CURRENCY_SYMBOLS, QuickOrderAdditionalFee } from '../../../types';
import { QuickOrderProduct } from '../../../types';
import { useTranslation } from 'react-i18next';
import { translateAdditionalFee } from '../../../i18n/utils';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { calculateOrderTotals } from '../../../common/util';

export const QuickOrderTotals = ({ products, additionalFees, currency }: { products: QuickOrderProduct[], additionalFees: Pick<QuickOrderAdditionalFee, 'id' | 'name' | 'price' | 'description' | 'type'>[], currency: Currencies }) => {
	const { subTotal, fixedFees, calculatedFees, total } = calculateOrderTotals({ products, additionalFees });
	const { t } = useTranslation();
	const currencySymbol = CURRENCY_SYMBOLS[currency];
	return <List sx={{pb:0}}>
		{Boolean(calculatedFees.length || fixedFees.length) && <ListItem key='subtotal'>
			<Typography flex={1}>{t('planViewCheckout.subtotal')}</Typography>
			<Typography>{currencySymbol}{subTotal.toFixed(2)}</Typography>
		</ListItem>}
		{fixedFees.length ? <Divider /> : null}
		{fixedFees.map((fee) => (<>
			<ListItem key={fee.id}>
				<Typography flex={1}>{translateAdditionalFee(fee.name)}</Typography>
				<Typography>{currencySymbol}{Number(fee.price).toFixed(2)}</Typography>
			</ListItem>
		</>))}
		{calculatedFees.length ? <Divider /> : null}
		{calculatedFees.map((fee) => (<>
			<ListItem key={fee.id}>
				<Typography flex={1}>{translateAdditionalFee(fee.name)} ({fee.price}%)</Typography>
				<Typography>{currencySymbol}{fee.sum}</Typography>
			</ListItem>
		</>))}
		{Boolean(calculatedFees.length || fixedFees.length) && <Divider />}
		<ListItem>
			<Typography flex={1} fontWeight='bold'>{t('planViewCheckout.total')}</Typography>
			<Typography fontWeight='bold'>{currencySymbol}{total.toFixed(2)}</Typography>
		</ListItem>
		{/* <ListItem>
			<Typography fontSize='small'>
				{t('planViewCheckout.vatIncludedNotice')}
			</Typography>
		</ListItem> */}
	</List>;
};

export const QuickOrderSellerTotals = ({ products, additionalFees, currency }: { products: { pricePerUnit: string, quantity: string }[], additionalFees: Pick<QuickOrderAdditionalFee, 'id' | 'name' | 'price' | 'description' | 'type'>[], currency: Currencies }) => {
	const { subTotal, fixedFees, calculatedFees, total } = calculateOrderTotals({ products, additionalFees });
	const { t } = useTranslation();
	const currencySymbol = CURRENCY_SYMBOLS[currency];
	return <List sx={{pb:0, mt: 0}}>
		{Boolean(calculatedFees.length || fixedFees.length) && <ListItem key='subtotal'>
			<Typography flex={1}>{t('planViewCheckout.subtotal')}</Typography>
			<Typography>{currencySymbol}{subTotal.toFixed(2)}</Typography>
		</ListItem>}
		{fixedFees.length ? <Divider /> : null}
		{fixedFees.map((fee) => (<>
			<ListItem key={fee.id}>
				<Typography flex={1}>{t('checkout.shippingSelected', { deliveryMethod: translateAdditionalFee(fee.name) })}</Typography>
				<Typography>{currencySymbol}{Number(fee.price).toFixed(2)}</Typography>
			</ListItem>
		</>))}
		{calculatedFees.length ? <Divider /> : null}
		{calculatedFees.map((fee) => (<>
			<ListItem key={fee.id}>
				<Typography flex={1}>{translateAdditionalFee(fee.name)} ({fee.price}%)</Typography>
				<Typography>{currencySymbol}{fee.sum}</Typography>
			</ListItem>
		</>))}
		{Boolean(calculatedFees.length || fixedFees.length) && <Divider />}
		<ListItem>
			<Typography flex={1}>Total</Typography>
			<Typography fontWeight='bold'>{currencySymbol}{total.toFixed(2)}</Typography>
		</ListItem>
		{/* <ListItem>
			<Typography fontSize='small'>
				{t('planViewCheckout.vatIncludedNotice')}
			</Typography>
		</ListItem> */}
	</List>;
};

export const QuickOrderCombinedTotal = ({ products, additionalFees, currency, showCheckmark }: { products: { pricePerUnit: string, quantity: string }[], additionalFees: Pick<QuickOrderAdditionalFee, 'id' | 'name' | 'price' | 'description' | 'type'>[], currency: Currencies, showCheckmark?: boolean }) => {
	const { total } = calculateOrderTotals({ products, additionalFees });
	const currencySymbol = CURRENCY_SYMBOLS[currency];
	return <List sx={{pb:0}}>
		<ListItem>
			<Typography flex={1} fontWeight='bold'>Combined Total</Typography>
			<Box display='flex' flexDirection='row' gap={1} alignItems='center'>
				<Typography fontWeight='bold'>{currencySymbol}{total.toFixed(2)}</Typography>
				{showCheckmark && <CheckCircleIcon
					color='success'
					sx={{fontSize: 20}}
				/>}
			</Box>
		</ListItem>
	</List>;
};